import { Vector2, Vector3 } from 'three';

import { IWall } from '../abstract/Wall';
import StandardWallModel from './StandardWallModel';

export type PreviewWallGeometry = {
  pointC: Vector3;
  pointD: Vector3;
  pointC2: Vector3;
  pointD2: Vector3;
};

export type PreviewWallModelArgs = {
  uuid?: string;

  /**
   * The real clicked/docked start.
   * For free drawing this equals labelStartPoint.
   * For docked drawing this is the dock CENTERLINE point.
   */
  structuralStartPoint: Vector3;

  /**
   * The real clicked/docked end.
   * For free drawing this equals labelEndPoint.
   * For docked drawing this is the dock CENTERLINE point.
   */
  structuralEndPoint: Vector3;

  /**
   * Hidden CAD centerline used for the final StandardWallModel.
   */
  previewStartPoint: Vector3;
  previewEndPoint: Vector3;

  depth: number;
  geometry?: PreviewWallGeometry;

  /**
   * Architect-visible promised inner line.
   */
  labelStartPoint?: Vector3;
  labelEndPoint?: Vector3;
};

const createFallbackGeometry = (startPoint: Vector3, endPoint: Vector3, depth: number): PreviewWallGeometry => {
  const dir = new Vector2(endPoint.x - startPoint.x, endPoint.y - startPoint.y);

  if (dir.lengthSq() < 1e-9) {
    return {
      pointC: startPoint.clone(),
      pointD: endPoint.clone(),
      pointC2: startPoint.clone(),
      pointD2: endPoint.clone(),
    };
  }

  dir.normalize();

  const normal = new Vector3(-dir.y, dir.x, 0).multiplyScalar(depth / 2);

  return {
    pointC: startPoint.clone().add(normal),
    pointD: endPoint.clone().add(normal),
    pointC2: startPoint.clone().sub(normal),
    pointD2: endPoint.clone().sub(normal),
  };
};

class PreviewWallModel extends StandardWallModel {
  readonly isPreviewWallModel = true;

  structuralStartPoint: Vector3;

  structuralEndPoint: Vector3;

  previewStartPoint: Vector3;

  previewEndPoint: Vector3;

  pointC: Vector3;

  pointD: Vector3;

  pointC2: Vector3;

  pointD2: Vector3;

  labelStartPoint: Vector3;

  labelEndPoint: Vector3;

  constructor(args: PreviewWallModelArgs) {
    super(args.previewStartPoint.clone(), args.previewEndPoint.clone(), args.depth, [], [], [], [], args.uuid);

    this.structuralStartPoint = args.structuralStartPoint.clone();
    this.structuralEndPoint = args.structuralEndPoint.clone();

    this.previewStartPoint = args.previewStartPoint.clone();
    this.previewEndPoint = args.previewEndPoint.clone();

    this.depth = args.depth;

    const geometry = args.geometry ?? createFallbackGeometry(args.previewStartPoint, args.previewEndPoint, args.depth);

    this.pointC = geometry.pointC.clone();
    this.pointD = geometry.pointD.clone();
    this.pointC2 = geometry.pointC2.clone();
    this.pointD2 = geometry.pointD2.clone();

    this.labelStartPoint = args.labelStartPoint?.clone() ?? this.structuralStartPoint.clone();
    this.labelEndPoint = args.labelEndPoint?.clone() ?? this.structuralEndPoint.clone();
  }

  toStandardWallModel(): StandardWallModel {
    return new StandardWallModel(this.previewStartPoint.clone(), this.previewEndPoint.clone(), this.depth, [], [], [], [], this.uuid);
  }

  static isPreviewWall(wall: IWall | undefined | null): wall is PreviewWallModel {
    return !!wall && (wall as any).isPreviewWallModel === true;
  }
}

export default PreviewWallModel;